import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Shield,
  X,
  PenLine,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Type,
  Lock,
  ArrowRight,
  ArrowLeft,
  User,
  MapPin,
  Building2
} from "lucide-react";
import NdaSignaturePad, { SignaturePadHandle } from "./NdaSignaturePad";
import { signNda } from "../../utils/api/endpoints/questionnaire";
import { pdf } from "@react-pdf/renderer";
import { NdaPdfDocument } from "./NdaPdfDocument";

interface NdaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSigned: (signerName: string, signedAt: string) => void;
  token: string;
  clientName: string;
  ndaText: string;
  id?: string;
}

type ModalState = "idle" | "submitting" | "success" | "error";
type SignatureType = "draw" | "type";
type Step = 1 | 2 | 3;

const FONT_OPTIONS = [
  { name: "Elegant Cursive", value: "54px 'Dancing Script', cursive", class: "font-dancing" },
  { name: "Modern Script",   value: "50px 'Caveat', cursive",         class: "font-caveat"  },
  { name: "Bold Script",     value: "44px 'Pacifico', cursive",       class: "font-pacifico"}
];

const STEPS = [
  { num: 1, label: "Review NDA",      sub: "Read the agreement"      },
  { num: 2, label: "Your Details",    sub: "Add details & signature" },
  { num: 3, label: "Review & Submit", sub: "Confirm your document"   }
] as const;

const generateTypedSignaturePng = (text: string, fontVal: string): string => {
  const canvas = document.createElement("canvas");
  canvas.width = 500; canvas.height = 150;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = fontVal;
  ctx.fillStyle = "#0f1f5c";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  return canvas.toDataURL("image/png");
};

const NdaModal: React.FC<NdaModalProps> = ({
  isOpen,
  onClose,
  onSigned,
  token,
  clientName,
  ndaText,
  id
}) => {
  const signaturePadRef = useRef<SignaturePadHandle>(null);
  const iframeRef       = useRef<HTMLIFrameElement>(null);

  const [step,             setStep]             = useState<Step>(1);
  const [acknowledged,     setAcknowledged]     = useState(false);
  const [signerName,       setSignerName]       = useState("");
  const [signerAddress,    setSignerAddress]    = useState("");
  const [sigType,          setSigType]          = useState<SignatureType>("draw");
  const [selectedFont,     setSelectedFont]     = useState(FONT_OPTIONS[0]);
  const [hasSignature,     setHasSignature]     = useState(false);
  const [drawnSigBase64,   setDrawnSigBase64]   = useState<string | null>(null);
  const [modalState,       setModalState]       = useState<ModalState>("idle");
  const [errorMsg,         setErrorMsg]         = useState("");
  const [signedAt,         setSignedAt]         = useState<Date | null>(null);
  const [pdfUrl,           setPdfUrl]           = useState<string | null>(null);
  const [pdfLoading,       setPdfLoading]       = useState(true);
  const [signerCompanyName, setSignerCompanyName] = useState("");

  // Load Google Fonts once
  useEffect(() => {
    const linkId = "scoping-nda-fonts";
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId; link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Caveat:wght@700&family=Dancing+Script:wght@700&family=Pacifico&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  // Reset on open/close
  useEffect(() => {
    if (!isOpen) {
      setStep(1); setAcknowledged(false); setSignerName(""); setSignerAddress("");
      setSignerCompanyName("");
      setHasSignature(false); setDrawnSigBase64(null); setModalState("idle"); setErrorMsg(""); setSignedAt(null);
      setSigType("draw"); setSelectedFont(FONT_OPTIONS[0]); setPdfUrl(null); setPdfLoading(true);
    } else {
      setSignerCompanyName(clientName || "");
    }
  }, [isOpen, clientName]);

  // Build PDF blob when needed
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    let url: string | null = null;
    setPdfLoading(true);

    const buildPdf = async () => {
      try {
        let sig = "";
        if (step === 3) {
          if (sigType === "draw" && drawnSigBase64) {
            sig = drawnSigBase64;
          } else if (sigType === "type" && signerName.trim().length >= 2) {
            sig = generateTypedSignaturePng(signerName.trim(), selectedFont.value);
          }
        }
        const blob = await pdf(
          <NdaPdfDocument
            clientName={signerCompanyName || clientName}
            clientAddress={signerAddress}
            signerName={signerName}
            signerAddress={signerAddress}
            signatureBase64={sig || null}
            signerCompanyName={signerCompanyName || clientName}
            sessionId={id}
          />
        ).toBlob();
        if (active) {
          url = URL.createObjectURL(blob);
          setPdfUrl(url);
          setPdfLoading(false);
        }
      } catch (err) {
        console.error("NDA PDF error:", err);
        if (active) setPdfLoading(false);
      }
    };

    buildPdf();
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [isOpen, step, signerName, signerAddress, signerCompanyName, hasSignature, drawnSigBase64, sigType, selectedFont, clientName, id]);

  const canGoStep2 = acknowledged;
  const isSigValid = sigType === "draw" ? hasSignature : signerName.trim().length >= 2;
  const canGoStep3 = signerName.trim().length >= 2 && signerAddress.trim().length >= 4 && signerCompanyName.trim().length >= 2 && isSigValid;
  const canSubmit  = canGoStep3 && modalState === "idle";

  const handleNext = () => {
    if (step === 1 && canGoStep2) setStep(2);
    else if (step === 2 && canGoStep3) setStep(3);
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  const handleSign = async () => {
    if (!canSubmit) return;
    let signatureBase64 = "";
    if (sigType === "draw") {
      if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
        setErrorMsg("Please draw your signature before submitting."); return;
      }
      signatureBase64 = signaturePadRef.current.toDataURL("image/png");
    } else {
      signatureBase64 = generateTypedSignaturePng(signerName.trim(), selectedFont.value);
    }
    setModalState("submitting"); setErrorMsg("");
    try {
      const result = await signNda(token, {
        signerName: signerName.trim(),
        signerAddress: signerAddress.trim(),
        signatureBase64,
        clientName: signerCompanyName.trim()
      });
      setSignedAt(new Date(result.nda_signed_at));
      setModalState("success");
      setTimeout(() => onSigned(signerName.trim(), result.nda_signed_at), 1800);
    } catch (err: any) {
      const msg =
        err?.response?.status === 409 ? "This NDA has already been signed for this session." :
        err?.response?.status === 413 ? "Signature image size is too large." :
        err?.response?.data?.message || "Failed to sign NDA. Please try again.";
      setErrorMsg(msg); setModalState("error");
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm montserrat-font"
      role="dialog" aria-modal="true" aria-label="Non-Disclosure Agreement"
    >
      <div
        className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200/80"
        style={{ height: "96vh", maxHeight: "96vh", animation: "ndaFadeIn 0.2s ease-out" }}
      >
        {/* ── Modal Header ─────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-3">
            <Shield size={20} className="text-[#1b3a8c]" />
            <div>
              <h2 className="text-[15px] font-bold text-slate-900 leading-tight">
                Non-Disclosure Agreement (NDA)
              </h2>
              <p className="text-[11.5px] text-slate-400">Review and sign to continue</p>
            </div>
          </div>
          {modalState !== "submitting" && modalState !== "success" && (
            <button
              type="button" onClick={onClose}
              className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition"
            >
              <X size={15} />
            </button>
          )}
        </div>

        {/* ── Success State ─────────────────────────────────────── */}
        {modalState === "success" && (
          <div className="flex-1 flex flex-col items-center justify-center p-12 gap-5 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-50 border-2 border-emerald-400 flex items-center justify-center">
              <CheckCircle2 size={40} className="text-emerald-500" strokeWidth={1.5} />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-slate-900">NDA Signed Successfully</h3>
              <p className="text-sm text-slate-500 max-w-sm">
                Your signature has been recorded. Our team will counter-sign the NDA shortly.
              </p>
              {signedAt && (
                <p className="text-[11px] text-slate-400">
                  Signed on {signedAt.toLocaleString("en-NZ", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
              )}
            </div>
            <p className="text-xs text-[#1b3a8c] font-semibold animate-pulse">Unlocking your scoping form…</p>
          </div>
        )}

        {/* ── Main Body (sidebar + content) ────────────────────── */}
        {modalState !== "success" && (
          <div className="flex flex-1 overflow-hidden">

            {/* Left Sidebar */}
            <aside className="w-52 shrink-0 bg-slate-50 border-r border-slate-200 flex flex-col p-4 gap-2">
              {/* Step indicators */}
              <div className="space-y-1 mb-2">
                {STEPS.map((s) => {
                  const isActive   = step === s.num;
                  const isComplete = step > s.num;
                  return (
                    <div
                      key={s.num}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                        isActive ? "bg-[#0b1739] text-white" : "text-slate-500"
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        isActive   ? "bg-white text-[#0b1739]" :
                        isComplete ? "bg-emerald-100 text-emerald-600 border border-emerald-300" :
                                     "bg-slate-200 text-slate-500"
                      }`}>
                        {isComplete ? <CheckCircle2 size={14} /> : s.num}
                      </div>
                      <div className="min-w-0">
                        <p className={`text-[12px] font-bold leading-tight ${isActive ? "text-white" : "text-slate-700"}`}>
                          {s.label}
                        </p>
                        <p className={`text-[10px] leading-tight mt-0.5 ${isActive ? "text-slate-300" : "text-slate-400"}`}>
                          {s.sub}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>


            </aside>

            {/* Right Content Panel */}
            <div className="flex-1 flex flex-col overflow-hidden">

              {/* ── STEP 1: Review NDA ─────────────────────────── */}
              {step === 1 && (
                <>
                  <div className="px-6 pt-5 pb-3 shrink-0 border-b border-slate-100">
                    <h3 className="text-[14px] font-bold text-slate-900">Review the Agreement</h3>
                    <p className="text-[11.5px] text-slate-400 mt-0.5">Please read the full agreement before proceeding</p>
                  </div>

                  {/* PDF Viewer — fills all remaining height */}
                  <div className="flex-1 min-h-0 px-5 pt-4 pb-2 overflow-hidden">
                    <div className="h-full rounded-xl border border-slate-200 bg-slate-50 overflow-hidden shadow-inner relative">
                      {pdfLoading ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400">
                          <Loader2 size={22} className="animate-spin text-[#1b3a8c]" />
                          <span className="text-xs">Generating preview…</span>
                        </div>
                      ) : pdfUrl ? (
                        <iframe
                          ref={iframeRef}
                          src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
                          style={{ width: "100%", height: "100%", border: "none" }}
                          title="NDA PDF Preview"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">
                          Unable to generate preview.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Confirmation checkbox — fixed strip */}
                  <div className="px-5 py-3 shrink-0">
                    <div
                      className="flex items-start gap-3 p-3.5 rounded-xl border border-slate-200 bg-white cursor-pointer hover:border-slate-300 transition select-none"
                      onClick={() => setAcknowledged((v) => !v)}
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                          acknowledged ? "bg-[#0b1739] border-[#0b1739]" : "border-slate-300 bg-white"
                        }`}
                      >
                        {acknowledged && (
                          <svg viewBox="0 0 10 8" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M1 4l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <span className="text-[12.5px] text-slate-600 leading-relaxed">
                        I confirm that I have read and understood this Non-Disclosure Agreement and agree to be bound by all its terms and conditions.
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* ── STEP 2: Your Details & Signature ───────────────────────── */}
              {step === 2 && (
                <>
                  <div className="px-6 pt-5 pb-3 shrink-0 border-b border-slate-100">
                    <h3 className="text-[14px] font-bold text-slate-900">Your Details</h3>
                    <p className="text-[11.5px] text-slate-400 mt-0.5">Please provide your information to complete the agreement</p>
                  </div>
                  <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                    {/* Fields Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[12px] font-semibold text-slate-600 mb-1.5">
                          <span className="flex items-center gap-1.5">
                            <User size={12} /> Full Legal Name
                          </span>
                        </label>
                        <input
                          type="text"
                          value={signerName}
                          onChange={(e) => setSignerName(e.target.value)}
                          placeholder="e.g. Jane Smith"
                          maxLength={200}
                          className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-slate-50
                                     text-sm text-slate-900 placeholder:text-slate-400
                                     focus:outline-none focus:bg-white focus:border-[#1b3a8c]
                                     focus:ring-4 focus:ring-[#1b3a8c]/10 transition"
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] font-semibold text-slate-600 mb-1.5">
                          <span className="flex items-center gap-1.5">
                            <Building2 size={12} /> Company / Organization
                          </span>
                        </label>
                        <input
                          type="text"
                          value={signerCompanyName}
                          onChange={(e) => setSignerCompanyName(e.target.value)}
                          placeholder="e.g. Acme Security Ltd."
                          maxLength={200}
                          className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-slate-50
                                     text-sm text-slate-900 placeholder:text-slate-400
                                     focus:outline-none focus:bg-white focus:border-[#1b3a8c]
                                     focus:ring-4 focus:ring-[#1b3a8c]/10 transition"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[12px] font-semibold text-slate-600 mb-1.5">
                        <span className="flex items-center gap-1.5">
                          <MapPin size={12} /> Physical / Company Address
                        </span>
                      </label>
                      <input
                        type="text"
                        value={signerAddress}
                        onChange={(e) => setSignerAddress(e.target.value)}
                        placeholder="e.g. 123 Main Street, Auckland 1010, New Zealand"
                        maxLength={500}
                        className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-slate-50
                                   text-sm text-slate-900 placeholder:text-slate-400
                                   focus:outline-none focus:bg-white focus:border-[#1b3a8c]
                                   focus:ring-4 focus:ring-[#1b3a8c]/10 transition"
                      />
                    </div>

                    <div className="border-t border-slate-200/80 my-2" />

                    {/* Signature Area */}
                    <div>
                      <h4 className="text-[13px] font-bold text-slate-900 mb-1">Your Signature</h4>
                      <p className="text-[11.5px] text-slate-400 mb-3">Sign your name below</p>
                      
                      <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 w-fit mb-4">
                        {[
                          { key: "draw" as SignatureType, icon: <PenLine size={11} />, label: "Draw" },
                          { key: "type" as SignatureType, icon: <Type size={11} />,    label: "Type" }
                        ].map(({ key, icon, label }) => (
                          <button
                            key={key} type="button" 
                            onClick={() => {
                              setSigType(key);
                              setHasSignature(false);
                              setDrawnSigBase64(null);
                            }}
                            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[11px] font-bold transition-all ${
                              sigType === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                            }`}
                          >
                            {icon} {label}
                          </button>
                        ))}
                      </div>

                      {sigType === "draw" ? (
                        <NdaSignaturePad
                          ref={signaturePadRef}
                          onEnd={() => {
                            const isPadEmpty = signaturePadRef.current?.isEmpty() ?? true;
                            setHasSignature(!isPadEmpty);
                            if (!isPadEmpty && signaturePadRef.current) {
                              setDrawnSigBase64(signaturePadRef.current.toDataURL("image/png"));
                            } else {
                              setDrawnSigBase64(null);
                            }
                          }}
                        />
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {FONT_OPTIONS.map((f, idx) => (
                            <button
                              key={idx} type="button" onClick={() => setSelectedFont(f)}
                              className={`p-4 border-2 rounded-xl text-left bg-slate-50/30 hover:bg-slate-50 transition-all ${
                                selectedFont.value === f.value ? "border-[#0b1739] bg-white shadow-md" : "border-slate-200"
                              }`}
                            >
                              <span className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">{f.name}</span>
                              <span className={`block text-xl text-[#0b1739] select-none truncate ${f.class}`} style={{ fontFamily: f.class }}>
                                {signerName.trim() || "Your Name"}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-[11.5px] text-slate-500 leading-relaxed">
                      <p>
                        By signing below, <strong className="text-slate-800">{signerName.trim() || clientName}</strong> agrees
                        to the terms of the Non-Disclosure Agreement with{" "}
                        <strong className="text-slate-800">Capture The Bug Limited</strong>.
                        This digital signature is legally binding.
                      </p>
                    </div>

                    <p className="text-[11px] text-slate-400 flex items-center gap-1">
                      <Lock size={12} className="text-slate-450" />
                      Signing records your name, email, IP address and a timestamp alongside the agreement.
                    </p>

                    {errorMsg && (
                      <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
                        <AlertCircle size={14} className="shrink-0 mt-0.5" />
                        {errorMsg}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── STEP 3: Review & Submit ─────────────────────── */}
              {step === 3 && (
                <>
                  <div className="px-6 pt-5 pb-3 shrink-0 border-b border-slate-100">
                    <h3 className="text-[14px] font-bold text-slate-900">Review Your Signed Document</h3>
                    <p className="text-[11.5px] text-slate-400 mt-0.5">Please review the final document with your details and signature before submitting</p>
                  </div>

                  {/* Final PDF preview — fills all remaining height */}
                  <div className="flex-1 min-h-0 px-5 pt-4 pb-2 overflow-hidden">
                    <div className="h-full rounded-xl border border-slate-200 bg-slate-50 overflow-hidden shadow-inner relative">
                      {pdfLoading ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400">
                          <Loader2 size={22} className="animate-spin text-[#1b3a8c]" />
                          <span className="text-xs">Building your signed document…</span>
                        </div>
                      ) : pdfUrl ? (
                        <iframe
                          src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
                          style={{ width: "100%", height: "100%", border: "none" }}
                          title="NDA Final Preview"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">
                          Unable to generate preview.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit confirmation strip */}
                  <div className="px-5 py-3 shrink-0">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                      <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                      <p className="text-[12px] text-emerald-850 leading-relaxed">
                        Everything looks good? Click <strong>Submit NDA</strong> to finalize and submit your agreement.
                      </p>
                    </div>
                  </div>

                  {errorMsg && (
                    <div className="mx-5 mb-2 flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
                      <AlertCircle size={14} className="shrink-0 mt-0.5" />
                      {errorMsg}
                    </div>
                  )}
                </>
              )}

              {/* ── Footer ────────────────────────────────────── */}
              <div className="px-6 py-4 border-t border-slate-200 shrink-0 flex items-center justify-between gap-4 bg-white">
                {step > 1 ? (
                  <button
                    type="button" onClick={handleBack}
                    disabled={modalState === "submitting"}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                ) : (
                  <div />
                )}

                {step < 3 ? (
                  <button
                    type="button" onClick={handleNext}
                    disabled={step === 1 ? !canGoStep2 : !canGoStep3}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-[#0b1739] text-white text-[13px] font-bold hover:bg-[#0b1739]/90 transition disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <Lock size={13} />
                    {step === 1 ? "Sign & Continue" : "Preview Document"}
                    <ArrowRight size={13} />
                  </button>
                ) : (
                  <button
                    type="button" onClick={handleSign}
                    disabled={!canSubmit}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-[#0b1739] text-white text-[13px] font-bold hover:bg-[#0b1739]/90 transition disabled:opacity-40 disabled:pointer-events-none"
                  >
                    {modalState === "submitting" ? (
                      <><Loader2 size={14} className="animate-spin" /> Signing…</>
                    ) : (
                      <><CheckCircle2 size={14} /> Submit NDA</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes ndaFadeIn {
          from { opacity: 0; transform: scale(0.97); }
          to   { opacity: 1; transform: scale(1); }
        }
        .font-dancing  { font-family: 'Dancing Script', cursive !important; }
        .font-caveat   { font-family: 'Caveat', cursive !important; }
        .font-pacifico { font-family: 'Pacifico', cursive !important; }
      `}</style>
    </div>,
    document.body
  );
};

export default NdaModal;
